/**
 * <mw-cart> — MiniWe cart page behaviour (sections/mw-cart.liquid).
 *
 * - Quantity / remove act on a figurine line and its add-on lines (data-children) together;
 *   add-ons and upgrades are charged per figurine, so their quantity follows the figurine.
 * - The per-line Sculpting time select swaps that figurine's Priority/Rush line.
 * - Orphaned add-on lines (data-orphans) are removed.
 * - Discount code forms ([data-discount-form], which post to /cart/update without JS) apply a code
 *   on top of the applied ones, or remove one; refused codes show a message after the re-render.
 * After each change the section is re-rendered with the Section Rendering API and the header
 * badge ([data-mw-cart-count]) is updated with the new figurine count; open detail panels stay open.
 */
class MwCart extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;

  connectedCallback() {
    this.#abort = new AbortController();
    const options = { signal: this.#abort.signal };
    this.addEventListener('click', (event) => this.#onClick(event), options);
    this.addEventListener('change', (event) => this.#onChange(event), options);
    this.addEventListener('submit', (event) => this.#onSubmit(event), options);

    /** @type {Record<string, number>} */
    const orphans = {};
    for (const key of (this.dataset.orphans || '').split(',').filter(Boolean)) orphans[key] = 0;
    if (Object.keys(orphans).length) this.#run(() => this.#update(orphans));
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  /** @param {MouseEvent} event */
  #onClick(event) {
    const button = event.target instanceof Element ? event.target.closest('button') : null;
    const line = button?.closest('[data-line]');
    if (!button || !(line instanceof HTMLElement)) return;
    const quantity = Number(line.dataset.quantity);
    if (button.hasAttribute('data-remove')) this.#setQuantity(line, 0);
    else if (button.dataset.quantityChange) this.#setQuantity(line, Math.max(0, quantity + Number(button.dataset.quantityChange)));
  }

  /** @param {Event} event */
  #onChange(event) {
    const select = event.target;
    const line = select instanceof HTMLSelectElement && select.matches('[data-sculpting]') ? select.closest('[data-line]') : null;
    if (!(select instanceof HTMLSelectElement) || !(line instanceof HTMLElement)) return;

    /** @type {Record<string, number>} */
    const removals = {};
    for (const key of (select.dataset.current || '').split(',').filter(Boolean)) removals[key] = 0;
    this.#run(async () => {
      if (Object.keys(removals).length) await this.#update(removals);
      if (select.value) {
        await this.#request(this.dataset.cartAddUrl || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{ id: Number(select.value), quantity: Number(line.dataset.quantity) || 1, properties: { _mw_group: line.dataset.group } }],
          }),
        });
      }
      await this.#setSculptingProperty(line, select.selectedOptions[0]?.dataset.label || '');
    });
  }

  /**
   * Keeps the figurine's visible "Sculpting time" property (read by the team on the order) in step
   * with the upgrade line it is charged for. Changing properties gives the line a new key.
   * @param {HTMLElement} line
   * @param {string} label
   */
  async #setSculptingProperty(line, label) {
    const cart = await this.#request(this.dataset.cartUrl || '/cart.js');
    const item = cart.items?.find((/** @type {{ key: string }} */ i) => i.key === line.dataset.line);
    if (!item || !label || item.properties?.['Sculpting time'] === label) return;
    await this.#request(this.dataset.cartChangeUrl || '/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: item.key, quantity: item.quantity, properties: { ...item.properties, 'Sculpting time': label } }),
    });
  }

  /** @param {SubmitEvent} event */
  #onSubmit(event) {
    const form = event.target;
    const input = form instanceof HTMLFormElement && form.matches('[data-discount-form]') ? form.elements.namedItem('discount') : null;
    if (!(input instanceof HTMLInputElement)) return;
    event.preventDefault();

    // The visible field adds a code; a pill's hidden field already holds the codes to keep.
    const adding = input.type !== 'hidden';
    const code = input.value.trim();
    const applied = Array.from(this.querySelectorAll('[data-discount-code]'), (el) => (el instanceof HTMLElement ? el.dataset.discountCode || '' : ''));
    if (adding && (!code || applied.some((c) => c.toLowerCase() === code.toLowerCase()))) return;

    /** @param {string} discount */
    const setCodes = (discount) =>
      this.#request(this.dataset.cartUpdateUrl || '/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount }),
      });

    /** @type {'' | 'code' | 'shipping'} */
    let error = '';
    this.#run(async () => {
      const data = await setCodes(adding ? [...applied, code].join(',') : code);
      const result = adding ? data.discount_codes?.find((/** @type {{ code: string }} */ d) => d.code.toLowerCase() === code.toLowerCase()) : null;
      if (!result) return;
      // Shipping discounts are accepted but only show up at checkout, so they render no pill.
      error = result.applicable ? 'shipping' : 'code';
      // Don't leave a refused code on the cart (checkout would flag it again).
      if (!result.applicable) await setCodes(applied.join(','));
    }).then((fresh) => {
      const details = fresh?.querySelector('.mw-cart__discount');
      if (details instanceof HTMLDetailsElement) details.open = true;
      const field = fresh?.querySelector('.mw-cart__discount-form input[name="discount"]');
      const shown = Array.from(fresh?.querySelectorAll('[data-discount-code]') ?? [], (el) => (el instanceof HTMLElement ? el.dataset.discountCode?.toLowerCase() : ''));
      if (error === 'shipping' && shown.includes(code.toLowerCase())) error = '';
      const message = fresh?.querySelector('[data-discount-error]');
      if (error && message instanceof HTMLElement) {
        message.textContent = (error === 'code' ? message.dataset.codeMessage : message.dataset.shippingMessage) || '';
        message.hidden = false;
      }
      if (field instanceof HTMLInputElement) field.focus();
    });
  }

  /**
   * @param {HTMLElement} line
   * @param {number} quantity
   */
  #setQuantity(line, quantity) {
    /** @type {Record<string, number>} */
    const updates = { [line.dataset.line || '']: quantity };
    for (const key of (line.dataset.children || '').split(',').filter(Boolean)) updates[key] = quantity;
    this.#run(() => this.#update(updates));
  }

  /** @param {Record<string, number>} updates */
  #update(updates) {
    return this.#request(this.dataset.cartUpdateUrl || '/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates }),
    });
  }

  /**
   * @param {() => Promise<unknown>} task
   * @returns {Promise<HTMLElement | null>} The re-rendered cart element.
   */
  async #run(task) {
    this.setAttribute('aria-busy', 'true');
    try {
      await task();
    } catch (error) {
      console.error(error);
    }
    return this.#render();
  }

  /**
   * Re-renders this section and refreshes the header cart count.
   * @returns {Promise<HTMLElement | null>}
   */
  async #render() {
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', this.dataset.sectionId || '');
    const html = await fetch(url).then((r) => r.text());
    const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('mw-cart');
    if (fresh) {
      // Keep the lines' detail panels that were open (a sculpting change gives the line a new key).
      const lineId = (/** @type {Element} */ details) => {
        const line = details.closest('[data-line]');
        return line instanceof HTMLElement ? line.dataset.group || line.dataset.line : '';
      };
      const open = new Set(Array.from(this.querySelectorAll('.mw-cart__details[open]'), lineId));
      for (const details of fresh.querySelectorAll('.mw-cart__details')) {
        if (details instanceof HTMLDetailsElement && open.has(lineId(details))) details.open = true;
      }
      this.replaceWith(fresh);
    } else this.removeAttribute('aria-busy');

    const badge = document.querySelector('[data-mw-cart-count]');
    if (badge instanceof HTMLElement && fresh instanceof HTMLElement) {
      badge.textContent = fresh.dataset.figurines || '0';
      badge.hidden = !Number(fresh.dataset.figurines);
    }
    return fresh instanceof HTMLElement ? fresh : null;
  }

  /**
   * @param {string} url
   * @param {RequestInit} [init]
   */
  async #request(url, init = {}) {
    const response = await fetch(url, { ...init, headers: { Accept: 'application/json', ...(init.headers || {}) } });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.description || 'Cart update failed');
    return data;
  }
}

if (!customElements.get('mw-cart')) customElements.define('mw-cart', MwCart);
